import { addMinutes, differenceInSeconds, isAfter, isBefore, subMinutes } from 'date-fns'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { v4 as uuid } from 'uuid'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import { BillGenerator, KitchenOrderGenerator, ProductPriceCalculator } from '@last/core'

import App from '@/app'
import { useNotifications } from '@/composables/useNotifications'
import { useTabs } from '@/composables/useTabs'
import { t } from '@/i18n'
import CashMachine from '@/integrations/cashmachine/cashmachine'
import Dataphone from '@/integrations/dataphone/dataphone'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import localDb from '@/localDb'
import { logger, rum } from '@/monitoring'
import { useAuthStore } from '@/store/auth'
import { useCatalogStore } from '@/store/catalog'
import { useConfigStore } from '@/store/config'
import { useFloorplanStore } from '@/store/floorplan'
import { usePromotionsStore } from '@/store/promotions'
import { useTillStore } from '@/store/till'
import sync from '@/sync/service'
import type {
  Bill,
  BillWithPayments,
  CatalogModifier,
  CatalogProductNormalized,
  Company,
  Courier,
  DeliveryStatus,
  Discount,
  KitchenOrder,
  NewTab,
  OrderingMode,
  Payment,
  PickupType,
  ProductDiscount,
  SentToKitchenProduct,
  Tab,
  TabComboProduct,
  TabProduct
} from '@/types'
import type { CustomerInfo } from '@/types/customerInfo'
import { normalizeTabs } from '@/utils/tabs.utils'

import { useFastMode } from './fastMode'

type ProductPricing = {
  fullPrice: number
  finalPrice: number
}

export const useTabsStore = defineStore('tabs', () => {
  const tabs = ref<Record<string, Tab>>({})
  const products = ref<Record<string, TabProduct>>({})
  const bills = ref<Record<string, Bill>>({})
  const billsMetadata = ref<Record<string, any>>({})
  const payments = ref<Record<string, Payment>>({})
  const kitchenOrders = ref<Record<string, KitchenOrder>>({})
  /** @deprecated */
  const refundablePaymentMethods = ref(['dataphone'])

  const floorplanStore = useFloorplanStore()
  const configStore = useConfigStore()
  const promotionsStore = usePromotionsStore()
  const authStore = useAuthStore()
  const tillStore = useTillStore()

  const route = useRoute()

  const parseKitchenProducts = computed(() => {
    return (tabId: string, products: TabProduct[]): any[] => {
      const catalogStore = useCatalogStore()
      return products.map(product => {
        let productCatalogId: string | undefined
        if (product.fromCombo && product.combo) {
          productCatalogId = catalogStore.getProductById(product.combo.parentProduct)?.catalogId
        }
        const originalProduct = catalogStore.getProductById(
          product.parentProduct!,
          productCatalogId
        )

        let categoryPosition = 0
        if (product.fromCombo && product.combo) {
          const originalCombo: CatalogProductNormalized = catalogStore.getProductById(
            product.combo.parentProduct
          )

          categoryPosition =
            originalCombo?.categories?.reduce(
              (res: Record<string, number>, category, categoryIndex: number) => {
                category.products.forEach(p => (res[p.id] = categoryIndex))
                return res
              },
              {}
            )[originalProduct?.id] || 0
        }

        let modifiers = product.modifiers || []
        if (product.modifiers && originalProduct && originalProduct.modifierGroups) {
          const originalModifiers = originalProduct.modifierGroups
            .flatMap(group => {
              return group.modifiers.map(modifier => {
                return {
                  ...modifier,
                  fullPosition: group.position * 1000 + modifier.position
                }
              })
            })
            .reduce((res: Record<string, CatalogModifier>, modifier) => {
              res[modifier.id] = modifier
              return res
            }, {})

          modifiers =
            product.modifiers &&
            product.modifiers
              .map(modifier => ({
                ...modifier,
                fullPosition:
                  modifier &&
                  modifier.parentModifierId &&
                  originalModifiers[modifier.parentModifierId]
                    ? originalModifiers[modifier.parentModifierId].fullPosition
                    : undefined,
                kitchenName:
                  modifier &&
                  modifier.parentModifierId &&
                  originalModifiers[modifier.parentModifierId]
                    ? originalModifiers[modifier.parentModifierId].kitchenName
                    : undefined
              }))
              .sort((a, b) => (a.fullPosition || 0) - (b.fullPosition || 0))
        }
        const category = catalogStore.categories[product.categoryId]
        let defaultPrinters: string[] = []
        const config = useConfigStore()
        if (config.config.defaultKitchenPrinter) {
          defaultPrinters = [config.config.defaultKitchenPrinter]
        }
        const printerIds =
          (originalProduct && originalProduct.printerIds) ||
          (category && category.printerIds) ||
          defaultPrinters

        return {
          ...originalProduct,
          ...product,
          modifiers,
          printerIds,
          copies: (originalProduct && originalProduct.copies) ?? (category && category.copies) ?? 1,
          categoryPosition
        }
      })
    }
  })

  const getPayment = computed(() => {
    return (id: string) => {
      return payments.value[id]
    }
  })

  function formatTab({ tab, tableIds = [] }: { tab: NewTab; tableIds?: string[] }) {
    const table = floorplanStore.tables[tableIds[0]]
    const virtualBrandId = tab.virtualBrandId || configStore.config.locationBrandId!
    const marginTime = virtualBrandId
      ? getActivationMarginPerVirtualBrandId.value(virtualBrandId)
      : 0
    const shouldBeActivated =
      !tab.schedulingTime ||
      isBefore(new Date(tab.schedulingTime), addMinutes(new Date(), marginTime))
    const now = new Date().toISOString()

    return {
      ...tab,
      id: tab.id || uuid(),
      tables: tableIds,
      open: true,
      source: tab.source || 'Restaurant',
      lang: tab.lang || configStore.config.locationCountryCode,
      creationTime: now,
      schedulingTime: tab.schedulingTime,
      activationTime: shouldBeActivated ? now : null,
      tableName: generateTableName(tab.name || '', tableIds),
      billingStartedTime: null,
      virtualBrandId,
      orderingMode: configStore.config.tabOrdering || 'seats',
      terraceSurchargePercentage: table?.terraceSurchargePercentage || 0,
      flatFeeSurcharge: table?.flatFeeSurcharge || 0
    }
  }

  const getBillById = computed(() => {
    return (billId: string): BillWithPayments => {
      const bill = bills.value[billId]
      const payment = bill.payments.map(paymentId => payments.value[paymentId])
      const metadata = billsMetadata.value[billId]

      return {
        ...bill,
        payments: payment,
        metadata
      }
    }
  })

  const getBills = computed(() => {
    return (tabId: string) => {
      const tab = tabs.value[tabId]
      if (!tab) return []
      return tab.bills
        .map(billId => ({
          ...bills.value[billId],
          payments: bills.value[billId].payments.map(paymentId => payments.value[paymentId])
        }))
        .map(bill =>
          BillGenerator.addPaymentInfo(
            bill,
            bill.payments.filter(payment => !!payment)
          )
        ) as BillWithPayments[]
    }
  })

  function generateTableName(tableName: string, tables: string[]) {
    const joinedName = tables
      .map(tableId => {
        const table = floorplanStore.tables[tableId]
        return table ? table.name : ''
      })
      .filter(name => name)
      .join(',')
    if (tableName) {
      return tableName + (joinedName ? ` (${joinedName})` : '')
    } else {
      return joinedName
    }
  }

  const getSentToKitchenProductsByTabId = computed(() => {
    return Object.keys(tabs.value).reduce(
      (res, tabId) => {
        res[tabId] = tabs.value[tabId].kitchenOrders
          .map(id => kitchenOrders.value[id])
          .flatMap(order =>
            order.versions.slice(-1)[0].products.map(product => {
              return {
                productId: product.tabProductId,
                sent: order.creationTime,
                kitchenOrder: order.id
              }
            })
          )
        return res
      },
      {} as Record<string, SentToKitchenProduct[]>
    )
  })

  const getSentToKitchenProducts = computed(() => {
    return (tabId: string): SentToKitchenProduct[] => {
      return getSentToKitchenProductsByTabId.value[tabId] || []
    }
  })

  const hasSentToKitchenProducts = computed(() => {
    return (tabId: string): boolean => {
      return getSentToKitchenProducts.value(tabId).length > 0
    }
  })

  const getSentToKitchenTime = computed(() => {
    return (productId: string) => {
      const product = products.value[productId]
      if (!product) return
      let productIds = [product.id]
      if (product.comboProducts && product.comboProducts.length > 0) {
        productIds = product.comboProducts.map(comboProduct => comboProduct.id)
      }
      const sentInfo = getSentToKitchenProducts
        .value(product.tab)
        .find(data => productIds.includes(data.productId))
      if (sentInfo) {
        return sentInfo.sent
      }
      return
    }
  })

  const getProductKitchenOrderIds = computed(() => {
    return (productId: string) => {
      const product = products.value[productId]
      let productIds = [product.id]
      if (product.comboProducts && product.comboProducts.length > 0) {
        productIds = product.comboProducts.map(comboProduct => comboProduct.id)
      }
      return getSentToKitchenProducts
        .value(product.tab)
        .filter(data => productIds.includes(data.productId))
        .map(sentInfo => sentInfo.kitchenOrder)
    }
  })

  const getDeliveryFee = computed(() => {
    return (tabId: string) => {
      const deliveryFee = { isFree: false, value: 0 }
      if (!tabs.value[tabId]) return deliveryFee
      const discount = getGlobalDiscountByTabId.value(tabId)
      const deliveryOrder = tabs.value[tabId].deliveryOrder
      if (discount && discount.freeDelivery) {
        deliveryFee.isFree = true
      } else if (deliveryOrder) {
        deliveryFee.value = deliveryOrder.deliveryFee || 0
      }

      return deliveryFee
    }
  })

  const getMinimumBasketSurcharge = computed(() => {
    return (tabId: string): number => {
      return tabs.value[tabId]?.deliveryOrder?.minimumBasketSurcharge || 0
    }
  })

  const getGlobalDiscountByTabId = computed(() => {
    return (tabId: string): Discount | undefined => {
      const promotion = promotionsStore.getTabGlobalPromotion(tabId)
      return promotion
        ? {
            type: promotion.discountType,
            amount: promotion.discountAmount,
            freeDelivery: promotion.freeDelivery
          }
        : undefined
    }
  })

  const getByTableId = computed(() => {
    return (tableId: string): Tab[] => {
      return Object.values(tabs.value).filter(tab => tab.open && tab.tables.includes(tableId))
    }
  })

  const getByBillId = computed(() => {
    return (billId: string): Tab | void => {
      return Object.values(tabs.value).find(tab => tab.bills.includes(billId))
    }
  })

  /** @deprecated */
  const getBillPayments = computed(() => {
    return (billId: string): Payment[] => {
      if (!bills.value[billId]) return []
      return bills.value[billId].payments.map(paymentId => payments.value[paymentId])
    }
  })

  /** @deprecated */
  const getNegativeTabPayments = computed(() => {
    return (tabId: string): Payment[] => {
      const tab = tabs.value[tabId]
      if (!tab) return []
      const negativePayments = []
      for (const billId of tab.bills) {
        const bill = bills.value[billId]
        for (const paymentId of bill.payments) {
          const payment = payments.value[paymentId]
          if (payment.amount < 0) {
            negativePayments.push(payment)
          }
        }
      }
      return negativePayments
    }
  })

  const getLastInteraction = computed(() => {
    return (tabId: string): Date => {
      const tab = tabs.value[tabId]
      return tab.kitchenOrders
        .map(id => kitchenOrders.value[id])
        .map(order => new Date(order.creationTime))
        .reduce((max, val) => {
          return new Date(Math.max(max.getTime(), val.getTime()))
        }, new Date(tab.creationTime))
    }
  })

  const getPendingBill = computed(() => {
    return (tabId: string): BillWithPayments | undefined => {
      const { allProducts } = useTabs(tabId)
      const products = allProducts.value
        .map(product => {
          return {
            ...product,
            quantity: product.notBilledQuantity ?? 0
          }
        })
        .filter(product => product.quantity > 0)
      if (products.length === 0) {
        return
      }
      return BillGenerator.generateProductsBill({
        products,
        company: configStore.config.company,
        tab: tabs.value[tabId],
        discount: getGlobalDiscountByTabId.value(tabId),
        taxRate: configStore.config.taxRate,
        ticketInfo: configStore.config.ticketInfo
      }) as BillWithPayments
    }
  })

  const isDeliveryTab = computed(() => {
    return (tabId: string) => {
      return tabs.value[tabId].deliveryOrder && tabs.value[tabId].pickupType !== 'takeAway'
    }
  })

  const getDeliveryTillId = computed(() => {
    return (): string | undefined => {
      const config = useConfigStore()
      const deliveryTillId = config.config.deliveryTillId
      if (deliveryTillId && config.tills.cash.some(till => till.id === deliveryTillId)) {
        return deliveryTillId
      }
    }
  })

  const getPaymentTillId = computed(() => {
    return (tabId: string, type: string): string | undefined => {
      if (isDeliveryTab.value(tabId)) {
        const deliveryTillId = getDeliveryTillId.value()
        if (deliveryTillId) return deliveryTillId
      }
      if (
        authStore.currentEmployee?.tillEnabled &&
        authStore.currentEmployee?.tillId &&
        type &&
        type === 'cash'
      ) {
        return authStore.currentEmployee?.tillId
      }
      if (tillStore.selectedCashTill) {
        return tillStore.selectedCashTill.id
      }
    }
  })

  const getTabPreparationMinutes = computed(() => {
    return (virtualBrandId: string): number => {
      const virtualBrand = configStore.virtualBrands?.find(b => b.id === virtualBrandId)
      const preparationMinutes = configStore.config.preparationMinutes
      const brandDeliveryConfig = virtualBrand?.deliveryConfig

      return brandDeliveryConfig?.preparationMinutes || preparationMinutes || 0
    }
  })

  const getActivationMarginPerVirtualBrandId = computed(() => {
    return (virtualBrandId: string): number => {
      const virtualBrand = configStore.virtualBrands?.find(b => b.id === virtualBrandId)
      const brandDeliveryConfig = virtualBrand?.deliveryConfig

      const preparationMargin = getTabPreparationMinutes.value(virtualBrandId)
      const schedulingMargin =
        brandDeliveryConfig?.schedulingMarginMinutes ||
        configStore.config.schedulingMarginMinutes ||
        0

      return Math.max(preparationMargin, schedulingMargin)
    }
  })

  function openTab({ tableIds, tab }: { tableIds: string[]; tab: NewTab }) {
    const newTab = formatTab({ tableIds, tab })
    sync.record('tabOpened', newTab)

    return newTab.id
  }

  function openTabWithCustomer({
    tableIds,
    tab,
    customer,
    reservationId
  }: {
    tableIds: string[]
    tab: NewTab
    customer: Partial<CustomerInfo>
    reservationId?: string
  }): string {
    const newTab = formatTab({ tableIds, tab })
    sync.record('tabOpenedWithCustomer', {
      tab: newTab,
      customer,
      reservationId
    })

    return newTab.id
  }

  function addProduct({
    tabId,
    seat,
    product
  }: {
    tabId: string
    seat: number | null
    product: TabProduct
  }): void {
    let finalTabId = tabId
    if (route.name === 'fastMode') {
      finalTabId = useFastMode().addTabToSystem() || tabId
    }
    const newProduct = {
      parentProduct: product.parentProduct,
      id: product.id || uuid(),
      name: product.name,
      price: product.price,
      fullPrice: product.fullPrice,
      finalPrice: product.finalPrice,
      discountType: product.discountType || null,
      discountAmount: product.discountAmount || null,
      discountConcept: product.discountConcept || null,
      promotionId: product.promotionId || null,
      pointsExpense: product.pointsExpense || 0,
      modifiers: product.modifiers,
      comboProducts: product.comboProducts,
      comments: product.comments,
      quantity: product.quantity || 1,
      tab: finalTabId,
      course: product.course || 'Main',
      categoryId: product.categoryId,
      combine: product.combine || false,
      seat,
      flatFeeSurcharge: product.flatFeeSurcharge || 0
    }
    sync.record('productAdded', {
      tabId: finalTabId,
      seat,
      product: newProduct
    })
    tabProductsUpdated(tabId)
  }

  function moveProductTab({
    productId,
    fromTabId,
    toTabId
  }: {
    productId: string
    fromTabId: string
    toTabId: string
  }): void {
    const product = products.value[productId]
    sync.record('productTabMoved', { product, fromTabId, toTabId })

    const tabProducts = tabs.value[fromTabId].seats?.reduce((acc, seat) => {
      return acc + seat.length
    }, tabs.value[fromTabId].shared?.length || 0)
    if (tabProducts === 0) {
      sync.record('tabClosedWithInfo', {
        tabId: fromTabId,
        closeTime: new Date().toISOString(),
        closedWithPin: false,
        showPaidNotification: false
      })
    }
    tabProductsUpdated(fromTabId)
    tabProductsUpdated(toTabId)
  }

  function moveProduct({
    tabId,
    seat,
    productId
  }: {
    tabId: string
    seat: number | null
    productId: string
  }) {
    const product = products.value[productId]
    sync.record('productMoved', { tabId, seat, product })
  }

  function setPreferredPaymentMethod({
    tabId,
    preferredPaymentMethod
  }: {
    tabId: string
    preferredPaymentMethod: string
  }): void {
    sync.record('preferredPaymentMethodSelected', {
      tabId,
      preferredPaymentMethod
    })
  }

  function updateProductModifiers({
    productId,
    modifiers,
    comments,
    productPricing
  }: {
    productId: string
    modifiers: CatalogModifier[]
    comments: string
    productPricing: ProductPricing
  }): void {
    sync.record('productModifiersUpdated', {
      productId,
      modifiers,
      comments,
      productPricing
    })
  }

  function updateComboProducts({
    comboId,
    products,
    productPricing
  }: {
    comboId: string
    products: TabComboProduct[]
    productPricing: ProductPricing
  }): void {
    sync.record('comboProductsUpdated', {
      comboId,
      products,
      productPricing: {
        fullPrice: Math.max(productPricing.fullPrice, 0),
        finalPrice: Math.max(productPricing.finalPrice, 0)
      }
    })
  }

  function splitProduct({ productId, seat }: { productId: string; seat: number }): string | void {
    const product = products.value[productId]
    if (product.quantity < 2) {
      return
    }
    updateProductQuantity({
      productId,
      quantity: product.quantity - 1
    })
    const splittedProductId: string = uuid()
    const splittedProduct: TabProduct = {
      ...product,
      modifiers: product.modifiers?.map(m => {
        return {
          ...m,
          id: uuid()
        }
      }),
      comboProducts: product.comboProducts?.map(cp => {
        return {
          ...cp,
          id: uuid(),
          modifiers: cp.modifiers?.map(m => {
            return {
              ...m,
              id: uuid()
            }
          })
        }
      }),
      id: splittedProductId,
      quantity: 1,
      promotionId: undefined
    }
    addProduct({
      tabId: product.tab,
      seat: seat,
      product: splittedProduct
    })

    const kitchenOrderIds = getProductKitchenOrderIds.value(productId)
    kitchenOrderIds
      .map(kitchenOrderId => kitchenOrders.value[kitchenOrderId])
      .forEach(order => {
        const version = KitchenOrderGenerator.generateOrderVersionForSplittedProduct(
          order.versions.slice(-1)[0].products,
          productId,
          splittedProduct
        )
        sync.record('kitchenOrderVersionAdded', {
          orderId: order.id,
          version
        })
      })

    return splittedProductId
  }

  function updateProductQuantity({
    productId,
    quantity
  }: {
    productId: string
    quantity: number
  }): void {
    sync.record('productQuantityUpdated', { productId, quantity })
    const tabId = products.value[productId].tab
    tabProductsUpdated(tabId)
  }

  function updateProductDiscount({
    productId,
    discount,
    productPricing
  }: {
    productId: string
    discount: ProductDiscount
    productPricing: ProductPricing
  }): void {
    sync.record('productDiscountUpdated', {
      productId,
      discount,
      productPricing
    })
  }

  function removeProduct(productId: string): void {
    const product = products.value[productId]
    if (!product) return

    const tabId = product.tab
    sync.record('productRemoved', productId)
    tabProductsUpdated(tabId)
  }

  function updateDeliveryOrderStatus({
    tabId,
    newStatus,
    courier
  }: {
    tabId: string
    newStatus: DeliveryStatus
    courier?: Courier
  }): void {
    sync.record('deliveryOrderStatusUpdated', {
      tabId,
      newStatus,
      courier,
      date: new Date()
    })
  }

  function removeSeat({
    tabId,
    selectedSeatIndex
  }: {
    tabId: string
    selectedSeatIndex: number
  }): void {
    sync.record('seatRemoved', { tabId, selectedSeatIndex })
  }

  function addBill({ tabId, bill }: { tabId: string; bill: BillWithPayments }): void {
    if (!bills.value[bill.id]) {
      if (tabs.value[tabId].deliveryOrder?.status === 'CREATED') {
        sync.record('deliveryOrderStatusUpdated', {
          tabId,
          newStatus: 'KITCHEN',
          date: new Date()
        })
      }
      if (bill.discount?.promotionId) {
        sync.record('promotionRedeemed', {
          promotionId: bill.discount?.promotionId
        })
      }
      sync.record('billAdded', { tabId, bill })
      startBilling(tabId)
    }
  }

  function updateBillPayers({ billId, payers }: { billId: string; payers: number | null }): void {
    sync.record('billPayersUpdated', { billId, payers })
  }

  function addInvoice({
    tabId,
    bill,
    customerCompany
  }: {
    tabId: string
    bill: BillWithPayments
    customerCompany: Company
  }): void {
    if (!bills.value[bill.id]) {
      if (tabs.value[tabId].deliveryOrder?.status === 'CREATED') {
        sync.record('deliveryOrderStatusUpdated', {
          tabId,
          newStatus: 'KITCHEN',
          date: new Date()
        })
      }
      if (bill.discount?.promotionId) {
        sync.record('promotionRedeemed', {
          promotionId: bill.discount?.promotionId
        })
      }
      const billWithCompany = { ...bill, customerCompany, payments: [] }
      sync.record('billAdded', { tabId, bill: billWithCompany })
      startBilling(tabId)
    } else {
      regenerateBill({ tabId, billId: bill.id, customerCompany })
    }
  }

  function generatePendingBill({
    tabId,
    bill,
    discount,
    payments = []
  }: {
    tabId: string
    bill?: BillWithPayments
    discount?: Discount
    payments?: Payment[]
  }): void {
    if (!bill) bill = getPendingBill.value(tabId)
    if (bill && discount) {
      bill.discount = discount
    }
    if (bill) {
      if (bill.products?.length || 0 > 0) {
        addBill({ tabId, bill })
        for (const payment of payments) {
          addPayment({
            ...payment,
            billId: bill.id,
            tabId
          })
        }
      }
    }
  }

  function removeTabBills(tabId: string): void {
    tabs.value[tabId].bills.forEach(billId => {
      sync.record('billRemoved', { tabId, billId })
    })
  }

  async function removeBill({ tabId, billId }: { tabId: string; billId: string }): Promise<void> {
    const { notifyError } = useNotifications()
    const hasPayments = bills.value[billId].payments.some(paymentId => {
      return payments.value[paymentId].deleted !== true
    })
    if (hasPayments) {
      notifyError({
        title: t('tabs.remove-bill-error'),
        description: t('tabs.refundable-payments-error')
      })
      return
    }
    const totalCashMachineAmount = bills.value[billId].payments
      .map(paymentId => {
        return payments.value[paymentId]
      })
      .filter(payment => CashMachine.methods.includes(payment.type))
      .reduce((sum, payment) => sum + payment.amount, 0)
    if (totalCashMachineAmount) {
      await CashMachine.payOut(totalCashMachineAmount)
    }
    sync.record('billRemoved', { tabId, billId })
  }

  function addPaymentRequest(
    paymentId: string,
    billId: string,
    toCharge: number,
    tip: number,
    terminalId: string,
    integratorId: string
  ) {
    sync.record('paymentRequestAdded', {
      paymentId,
      billId,
      amount: toCharge - tip,
      tip,
      terminalId,
      integratorId
    })
  }

  function changePaymentRequest(paymentId: string, status: string) {
    sync.record('paymentRequestChanged', { paymentId, status })
  }

  function addPayment({
    billId,
    paymentId,
    externalId,
    amount,
    change = 0,
    tip = 0,
    type,
    tillId,
    metadata = '',
    tabId
  }: {
    billId: string
    paymentId?: string
    externalId?: string
    amount: number
    change?: number
    tip?: number
    type: string
    tillId?: string
    metadata?: unknown
    tabId: string
  }): void {
    const payment: Payment = {
      id: paymentId || uuid(),
      externalId,
      amount,
      change,
      tip,
      type,
      creationTime: new Date(),
      metadata
    }

    if (tillId) {
      payment.tillId = tillId
    } else {
      payment.tillId = getPaymentTillId.value(tabId, type)
    }
    sync.record('paymentAdded', { billId, payment })
  }

  async function deletePayment({
    billId,
    paymentId
  }: {
    billId: string
    paymentId: string
  }): Promise<void> {
    const { notifyError } = useNotifications()
    const payment = payments.value[paymentId]
    const bill = bills.value[billId]
    if (CashMachine.methods.includes(payment.type)) {
      await CashMachine.payOut(payment.amount)
    }
    if (payment.type === 'dataphone') {
      const { error } = await Dataphone.refund(payment, bill.tabId).catch(err => {
        logger.error('Error refunding payment', err)
        return { error: true }
      })
      if (error) {
        notifyError({
          title: t('tabs.refund-payment-error')
        })
        return
      }
    }
    sync.record('paymentDeleted', { billId, paymentId })
  }

  function closeTab({
    tabId,
    closedWithPin = false
  }: {
    tabId: string
    closedWithPin?: boolean
  }): void {
    sync.record('tabClosedWithInfo', {
      tabId,
      closeTime: new Date().toISOString(),
      closedWithPin,
      showPaidNotification: false
    })
    const tab = tabs.value[tabId]
    if (tab && tab.pickupType === 'ownDelivery' && tab.deliveryOrder) {
      updateDeliveryOrderStatus({
        tabId,
        newStatus: 'CLOSED'
      })
    }
  }

  function reopenTab({ tabId }: { tabId: string }) {
    sync.record('tabReopened', {
      tabId
    })
  }

  function createKitchenOrderByProducts({
    tabId,
    products
  }: {
    tabId: string
    products: TabProduct[]
  }): string[] {
    const kitchenProducts = parseKitchenProducts.value(tabId, products).flatMap(product => {
      if (product.comboProducts) {
        return product.comboProducts.map((comboProduct: TabComboProduct) => ({
          ...comboProduct,
          quantity: comboProduct.quantity * product.quantity,
          fromCombo: true,
          combo: product
        }))
      } else {
        return [product]
      }
    })
    const printers = Object.values(configStore.printers)
    const orderVirtualBrandName = configStore.virtualBrands.filter(
      vb => vb.id === tabs.value[tabId].virtualBrandId
    )[0]?.name

    const tab = tabs.value[tabId]
    const tabFloorplan = floorplanStore.getTableFloorplan(tab.tables[0])

    const orders = KitchenOrderGenerator.generateOrders(
      kitchenProducts,
      tabs.value[tabId],
      tabs.value[tabId].deliveryOrder,
      tabs.value[tabId].tableName,
      authStore.currentEmployee?.name,
      printers,
      orderVirtualBrandName,
      {
        printerId: configStore.config.controlPrinter,
        floorplanControlPrinter: tabFloorplan?.controlPrinter
      }
    )

    return orders.map(order => {
      sync.record('kitchenOrderAdded', { tabId, order })

      return order.id
    })
  }

  function createKitchenOrders({ tabId, course }: { tabId: string; course: string }): string[] {
    const { unsentProducts } = useTabs(tabId)
    const parsedKitchenProducts = parseKitchenProducts.value(
      tabId,
      unsentProducts.value.filter(product => course === 'all' || product.course === course)
    )
    const printers = Object.values(configStore.printers)
    const orderVirtualBrandName = configStore.virtualBrands.filter(
      vb => vb.id === tabs.value[tabId].virtualBrandId
    )[0]?.name

    const tab = tabs.value[tabId]
    const tabFloorplan = floorplanStore.getTableFloorplan(tab.tables[0])

    const orders = KitchenOrderGenerator.generateOrders(
      parsedKitchenProducts,
      tabs.value[tabId],
      tabs.value[tabId].deliveryOrder,
      tabs.value[tabId].tableName,
      authStore.currentEmployee?.name,
      printers,
      orderVirtualBrandName,
      {
        printerId: configStore.config.controlPrinter,
        floorplanControlPrinter: tabFloorplan?.controlPrinter
      }
    )

    if (tab && tab.deliveryOrder?.status === 'CREATED') {
      sync.record('deliveryOrderStatusUpdated', {
        tabId,
        newStatus: 'KITCHEN',
        date: new Date()
      })
    }
    return orders.map(order => {
      sync.record('kitchenOrderAdded', { tabId, order })
      return order.id
    })
  }

  function addKitchenOrderVersion(orderId: string): string {
    const order = kitchenOrders.value[orderId]
    const { allProducts } = useTabs(order.tabId)
    const productIds = order.versions.slice(-1)[0].products.map(product => product.tabProductId)
    const products = parseKitchenProducts.value(
      order.tabId,
      allProducts.value
        .flatMap((product: TabProduct): TabProduct[] => {
          if (product.comboProducts && product.comboProducts.length > 0) {
            return product.comboProducts.map(
              comboProduct =>
                ({
                  ...comboProduct,
                  quantity: comboProduct.quantity * product.quantity,
                  fromCombo: true,
                  combo: product
                }) as unknown as TabProduct
            )
          } else {
            return [product]
          }
        })
        .filter(product => productIds.includes(product.id))
    )
    const version = KitchenOrderGenerator.generateOrderVersion(products)
    sync.record('kitchenOrderVersionAdded', { orderId, version })

    return order.id
  }

  type NormalizedTabsData = {
    products?: Record<string, TabProduct>
    tabs?: Record<string, Tab>
    bills?: Record<string, Bill>
    payments?: Record<string, Payment>
    kitchenOrders?: Record<string, KitchenOrder>
  }

  async function refreshCurrentTabs(tabsPayload: Tab[]): Promise<void> {
    const tabsData = normalizeTabs(tabsPayload)
    replaceTabs(tabsData)
    updateTabsActivationTime()
  }

  function replaceTabs(tabData: NormalizedTabsData): void {
    products.value = (tabData && tabData.products) || {}
    tabs.value = (tabData && tabData.tabs) || {}
    bills.value = (tabData && tabData.bills) || {}
    payments.value = (tabData && tabData.payments) || {}
    kitchenOrders.value = (tabData && tabData.kitchenOrders) || {}

    localDb.updateTables({
      'tabs:products': (tabData && tabData.products) || {},
      'tabs:tabs': (tabData && tabData.tabs) || {},
      'tabs:bills': (tabData && tabData.bills) || {},
      'tabs:payments': (tabData && tabData.payments) || {},
      'tabs:kitchenOrders': (tabData && tabData.kitchenOrders) || {}
    })
  }

  function refreshTab(tabData: NormalizedTabsData): void {
    products.value = { ...products.value, ...tabData.products }
    tabs.value = { ...tabs.value, ...tabData.tabs }
    bills.value = { ...bills.value, ...tabData.bills }
    payments.value = { ...payments.value, ...tabData.payments }
    kitchenOrders.value = { ...kitchenOrders.value, ...tabData.kitchenOrders }

    localDb.patchTables({
      'tabs:products': tabData.products || {},
      'tabs:tabs': tabData.tabs || {},
      'tabs:bills': tabData.bills || {},
      'tabs:payments': tabData.payments || {},
      'tabs:kitchenOrders': tabData.kitchenOrders || {}
    })
  }

  function assignTables({ tabId, tables }: { tabId: string; tables: string[] }): void {
    const tab = tabs.value[tabId]
    sync.record('tablesAssigned', {
      tabId,
      tables,
      tableName: generateTableName(tab.name, tables)
    })
  }

  function updateCourse({
    productId,
    course,
    comboProductId
  }: {
    productId: string
    course: string
    comboProductId?: string
  }): void {
    course = course || 'Main'
    if (!comboProductId) {
      sync.record('courseUpdated', { productId, course })
    } else {
      const comboProducts = [...products.value[productId].comboProducts].map(comboProduct => ({
        ...comboProduct,
        course: comboProduct.id === comboProductId ? course : comboProduct.course
      }))

      sync.record('comboProductsUpdated', {
        comboId: productId,
        products: comboProducts,
        productPricing: {
          fullPrice: Math.max(products.value[productId].fullPrice, 0),
          finalPrice: Math.max(products.value[productId].finalPrice, 0)
        }
      })
    }
  }

  function addSeat(tabId: string): void {
    sync.record('seatAdded', tabId)
  }

  function changePickupType({
    tabId,
    pickupType
  }: {
    tabId: string
    pickupType?: PickupType
  }): void {
    if (!tabs.value[tabId]) return
    sync.record('updateTabPickupType', { tabId, pickupType })
  }

  function swapTables({
    tableAId,
    tableBId,
    tabBId
  }: {
    tableAId: string
    tableBId: string
    tabBId: string
  }): void {
    const tabsA = Object.values(tabs.value).filter(tab => tab.tables.includes(tableAId))[0]
    const tabsB = tabs.value[tabBId]
    sync.record('tablesAssigned', {
      tabId: tabsA.id,
      tables: tabsA.tables.filter(id => id !== tableAId).concat([tableBId]),
      tableName: generateTableName(
        tabsA.name,
        tabsA.tables.filter(id => id !== tableAId).concat([tableBId])
      )
    })
    sync.record('tablesAssigned', {
      tabId: tabsB.id,
      tables: tabsB.tables.filter(id => id !== tableBId).concat([tableAId]),
      tableName: generateTableName(
        tabsB.name,
        tabsB.tables.filter(id => id !== tableBId).concat([tableAId])
      )
    })
  }

  function mergeTabs({ tabAId, tabBId }: { tabAId: string; tabBId: string }): void {
    const tabA = tabs.value[tabAId]
    const tabB = tabs.value[tabBId]
    const newTab: Tab = {
      id: uuid(),
      open: true,
      source: 'Restaurant',
      lang: tabA.lang,
      name: [tabA.name, tabB.name].filter(name => name).join(' '),
      pickupType: tabA.pickupType || tabB.pickupType,
      creationTime: new Date(
        Math.min(new Date(tabA.creationTime).getTime(), new Date(tabB.creationTime).getTime())
      ).toISOString(),
      activationTime: new Date(
        Math.min(new Date(tabA.activationTime!).getTime(), new Date(tabB.activationTime!).getTime())
      ).toISOString(),
      tableName: generateTableName([tabA.name, tabB.name].filter(name => name).join(' '), [
        ...tabA.tables,
        ...tabB.tables
      ]),
      terraceSurchargePercentage:
        Math.max(tabA.terraceSurchargePercentage || 0, tabB.terraceSurchargePercentage || 0) || 0,
      flatFeeSurcharge: Math.max(tabA.flatFeeSurcharge || 0, tabB.flatFeeSurcharge || 0) || 0,
      virtualBrandId: tabA.virtualBrandId || tabB.virtualBrandId
    }
    sync.record('mergeTabs', {
      tabAId,
      tabBId,
      newTab
    })
  }

  function regenerateBill({
    tabId,
    billId,
    customerCompany
  }: {
    tabId: string
    billId: string
    customerCompany?: Company
  }): string {
    let bill = bills.value[billId]
    if (!!bill.customerCompany !== !!customerCompany) {
      bill = BillGenerator.generateInvoice(bill, customerCompany)
    } else {
      bill = {
        ...bill,
        customerCompany
      }
    }
    sync.record('billRegenerated', { tabId, oldBillId: billId, bill })

    return bill.id
  }

  function startBilling(tabId: string) {
    if (!tabs.value[tabId].billingStartedTime) {
      sync.record('billingStarted', { tabId, timestamp: new Date().toISOString() })
    }
  }

  function deleteOldTabs(): void {
    const minSeconds = 60 * 60 * 24
    const oldTabs = Object.values(tabs.value)
      .filter(tab => tab.closeTime)
      .filter(tab => {
        const diff = differenceInSeconds(new Date(), new Date(tab.closeTime!))
        return diff > minSeconds
      })
    oldTabs.forEach(tab => deleteTabLocally({ tabId: tab.id }))
  }

  function deleteTabLocally({ tabId }: { tabId: string }): void {
    const tab = tabs.value[tabId]
    delete tabs.value[tabId]
    localDb.removeItem('tabs:tabs', tabId)
    tab.bills.forEach(billId => {
      const bill = bills.value[billId]
      bill.payments.forEach(paymentId => {
        delete payments.value[paymentId]
        localDb.removeItem('tabs:payments', paymentId)
      })
      delete bills.value[billId]
      delete billsMetadata.value[billId]
      localDb.removeItem('tabs:bills', billId)
    })
    tab.kitchenOrders.forEach(orderId => {
      delete kitchenOrders.value[orderId]
      localDb.removeItem('tabs:kitchenOrders', orderId)
    })
    tab.shared?.forEach(productId => {
      delete products.value[productId]
      localDb.removeItem('tabs:products', productId)
    })
    tab.seats?.flat().forEach(productId => {
      delete products.value[productId]
      localDb.removeItem('tabs:products', productId)
    })
  }

  function updateTabActivationTime({
    tabId,
    activationTime
  }: {
    tabId: string
    activationTime: string
  }): void {
    tabs.value[tabId].activationTime = activationTime
    const tab = tabs.value[tabId]
    tabs.value[tabId].bills.forEach(billId => {
      const bill = bills.value[billId]
      bills.value[billId] = {
        ...bill,
        activationTime,
        schedulingTime: tab?.schedulingTime
      }
    })
    tabs.value[tabId].kitchenOrders.forEach(orderId => {
      const order = kitchenOrders.value[orderId]
      kitchenOrders.value[orderId] = { ...order, activationTime }
    })
    localDb?.setItem('tabs:tabs', tabId, tabs.value[tabId])
  }

  async function activateTab({
    tab,
    sendEvent = false
  }: {
    tab: Tab
    sendEvent: boolean
  }): Promise<void> {
    if (sendEvent) {
      sync.record('updateTabActivationTime', {
        tabId: tab.id,
        activationTime: new Date().toISOString()
      })
      if (configStore.config.autoprintKitchenOrders) {
        createKitchenOrders({
          tabId: tab.id,
          course: 'all'
        })
        tabs.value[tab.id].kitchenOrders
          .map(orderId => kitchenOrders.value[orderId])
          .forEach(order => TicketPrinter.printKitchenOrder(order))
      }
      if (
        tabs.value[tab.id].kitchenOrders.length > 0 &&
        tab &&
        tab.deliveryOrder?.status === 'CREATED'
      ) {
        sync.record('deliveryOrderStatusUpdated', {
          tabId: tab.id,
          newStatus: 'KITCHEN',
          date: new Date()
        })
      }
      if (configStore.config.autoprintBills) {
        generatePendingBill({ tabId: tab.id })
        const bills = getBills.value(tab.id)
        bills.forEach(bill => TicketPrinter.printBill(bill))
      }
    } else {
      updateTabActivationTime({
        tabId: tab.id,
        activationTime: new Date().toISOString()
      })
    }
  }

  function updateTabsActivationTime() {
    if (!tillStore.shiftIsStarted && tillStore.shiftsEnabled) return

    const now = new Date()
    const config = useConfigStore()

    for (const tab of Object.values(tabs.value)) {
      try {
        if (!tab.schedulingTime || !!tab.activationTime) continue

        const marginTime = getActivationMarginPerVirtualBrandId.value(tab.virtualBrandId)
        const activationTime = subMinutes(
          new Date(tab.deliveryOrder?.pickupTime || tab.schedulingTime),
          marginTime
        )

        if (isAfter(now, activationTime)) {
          const isMaster = config.device.mode === 'master' && !!config.device.id
          if (isMaster) App.playSound('notification')

          activateTab({
            tab,
            sendEvent: isMaster
          })
        }
      } catch (error) {
        rum.addError(error)
      }
    }
  }

  function updateTerraceSurcharge({ tabId, enabled }: { tabId: string; enabled: boolean }): void {
    const table = floorplanStore.tables[tabs.value[tabId].tables[0]]
    if (table.surchargeType === 'flat') {
      const newValue = enabled ? table.flatFeeSurcharge || 0 : 0
      sync.record('flatFeeSurchargeUpdated', {
        tabId,
        newValue
      })
      const { allProducts } = useTabs(tabId)
      allProducts.value.forEach(product => {
        sync.record('productFlatFeeSurchargeUpdated', {
          productId: product.id,
          newValue,
          productPricing: ProductPriceCalculator.calculateProductPricing({
            ...product,
            flatFeeSurcharge: newValue
          })
        })
      })
    } else {
      const newPercentage = enabled ? table.terraceSurchargePercentage : 0
      sync.record('terraceSurchargePercentageUpdated', {
        tabId,
        newPercentage
      })
    }
  }

  function updateTabOrdering({
    tabId,
    orderingMode
  }: {
    tabId: string
    orderingMode: OrderingMode
  }): void {
    sync.record('tabOrderingUpdated', { tabId, orderingMode })
  }

  function tabProductsUpdated(tabId: string) {
    promotionsStore.recalculatePromotionsInProducts(tabId)
  }

  return {
    tabs,
    products,
    bills,
    billsMetadata,
    payments,
    kitchenOrders,
    refundablePaymentMethods,
    parseKitchenProducts,
    getPayment,
    getBillById,
    getBills,
    getSentToKitchenProductsByTabId,
    getSentToKitchenProducts,
    hasSentToKitchenProducts,
    getSentToKitchenTime,
    getProductKitchenOrderIds,
    getDeliveryFee,
    getMinimumBasketSurcharge,
    getGlobalDiscountByTabId,
    getByTableId,
    getByBillId,
    getBillPayments,
    getNegativeTabPayments,
    getLastInteraction,
    getPendingBill,
    isDeliveryTab,
    getDeliveryTillId,
    getPaymentTillId,
    getTabPreparationMinutes,
    getActivationMarginPerVirtualBrandId,
    formatTab,
    openTab,
    openTabWithCustomer,
    addProduct,
    moveProductTab,
    moveProduct,
    setPreferredPaymentMethod,
    updateProductModifiers,
    updateComboProducts,
    splitProduct,
    updateProductDiscount,
    updateProductQuantity,
    removeProduct,
    updateDeliveryOrderStatus,
    removeSeat,
    changePickupType,
    addBill,
    addInvoice,
    generatePendingBill,
    removeTabBills,
    removeBill,
    updateBillPayers,
    addPayment,
    addPaymentRequest,
    changePaymentRequest,
    deletePayment,
    closeTab,
    reopenTab,
    createKitchenOrderByProducts,
    createKitchenOrders,
    addKitchenOrderVersion,
    refreshCurrentTabs,
    replaceTabs,
    refreshTab,
    assignTables,
    updateCourse,
    addSeat,
    mergeTabs,
    regenerateBill,
    startBilling,
    deleteOldTabs,
    deleteTabLocally,
    updateTabActivationTime,
    activateTab,
    updateTabsActivationTime,
    updateTerraceSurcharge,
    updateTabOrdering,
    swapTables
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTabsStore, import.meta.hot))
}
